import { useState, useEffect, useMemo } from 'react';
import {
  ComposedChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid
} from 'recharts';
import { Box, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { CampaignGraphData } from '../../../network/Campaigns';

export default function CampaignStatsGraph({ startDate, endDate, interval }) {
  const [graphData, setGraphData] = useState([]);

  // Clean and format data similar to BatteryTrendGraph
  const cleaned = useMemo(() => {
    if (!Array.isArray(graphData)) return [];

    let arr = graphData.map((d) => {
      const ts = d.timestamp ?? d.time;
      const timestamp = ts ? new Date(ts).getTime() : NaN;
      const count = d.count ?? d.value ?? null;
      const cn = Number(count);

      return {
        timestamp,
        count: Number.isFinite(cn) ? cn : 0
      };
    });

    arr = arr.filter((d) => Number.isFinite(d.timestamp));
    arr.sort((a, b) => a.timestamp - b.timestamp);
    return arr;
  }, [graphData]);

  // Get first and last timestamps
  const firstTimestamp = cleaned.length > 0 ? cleaned[0].timestamp : null;
  const lastTimestamp = cleaned.length > 0 ? cleaned[cleaned.length - 1].timestamp : null;

  // Calculate ticks to always show first and last, with some intermediate ticks
  const xAxisTicks = useMemo(() => {
    if (!cleaned.length || !firstTimestamp || !lastTimestamp) return undefined;

    // Always include first and last
    const ticks = [firstTimestamp];

    // If we have more than 2 data points, add some intermediate ticks
    if (cleaned.length > 2) {
      const step = Math.ceil(cleaned.length / 3);
      for (let i = step; i < cleaned.length - 1; i += step) {
        if (cleaned[i].timestamp !== firstTimestamp && cleaned[i].timestamp !== lastTimestamp) {
          ticks.push(cleaned[i].timestamp);
        }
      }
    }

    // Always include last (if different from first)
    if (lastTimestamp !== firstTimestamp) {
      ticks.push(lastTimestamp);
    }

    return ticks;
  }, [cleaned, firstTimestamp, lastTimestamp]);

  // Time formatter
  const formatter = (ts) => {
    const dt = new Date(ts);
    return dt.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: false
    });
  };

  useEffect(() => {
    const fetchGraphData = async () => {
      // Use props if provided, otherwise use default (last 7 days)
      const today = dayjs();
      const start = startDate || today.subtract(7, 'day').format('YYYY-MM-DD');
      const end = endDate || today.format('YYYY-MM-DD');
      const intervalType = interval || 'minute';

      try {
        const res = await CampaignGraphData(start, end, intervalType);
        setGraphData(res?.data?.data ?? []);
      } catch (error) {
        console.error('Error fetching graph data', error);
        setGraphData([]);
      }
    };

    fetchGraphData();
  }, [startDate, endDate, interval]);

  // Calculate Y-axis domain
  const yAxisDomain = useMemo(() => {
    if (cleaned.length === 0) return [0, 10];
    const values = cleaned.map((d) => d.count);
    const min = Math.min(...values);
    const max = Math.max(...values);
    return [Math.max(0, min - 2), max + 2];
  }, [cleaned]);

  return (
    <Box
      sx={{
        // flex: 1,
        width: '100%',
        height: 220 // 🔥 VERY IMPORTANT for recharts inside flex
      }}
    >
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={cleaned} margin={{ top: 5, right: 20, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="campaignGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#39B449" stopOpacity={0.25} />
              <stop offset="100%" stopColor="#39B449" stopOpacity={0.05} />
            </linearGradient>
          </defs>

          <CartesianGrid stroke="#eee" vertical={false} />

          <XAxis
            dataKey="timestamp"
            tickFormatter={formatter}
            tick={{ fill: '#9AA0A6', fontSize: 12 }}
            minTickGap={40}
            axisLine={false}
            tickLine={false}
            domain={['dataMin', 'dataMax']}
            ticks={xAxisTicks}
          />

          <YAxis
            tick={{ fill: '#9AA0A6', fontSize: 12 }}
            axisLine={false}
            tickLine={false}
            domain={yAxisDomain}
            tickFormatter={(value) => (value === 0 ? '' : value)}
          />

          <Tooltip
            contentStyle={{
              backgroundColor: '#fff',
              borderRadius: 5,
              border: '1px solid #eee',
              padding: '12px 14px',
              fontSize: 14,
              color: '#333',
              textTransform: 'capitalize'
            }}
            labelStyle={{
              color: 'black',
              fontWeight: 700,
              marginBottom: 4,
              fontSize: 12
            }}
            itemStyle={{
              fontSize: '11px',
              color: '#555',
              marginTop: 2,
              lineHeight: '11px',
              fontWeight: 600
            }}
            labelFormatter={formatter}
            formatter={(v) => (v === null || v === undefined ? '-' : v)}
          />

          <Area
            type="monotone"
            dataKey="count"
            stroke="#39B449"
            fill="url(#campaignGrad)"
            strokeWidth={1.5}
            dot={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  );
}
